const DISCOUNT_SORT_VALUE = 'discount-descending';
const DISCOUNT_ORDER_VIEW = 'discount-order';
const DISCOUNT_CARDS_VIEW = 'discount-cards';
const DISCOUNT_ORDER_CONCURRENCY = 6;
const DISCOUNT_ORDER_PAGE_LIMIT = 40;
const DISCOUNT_BATCH_SIZE = 20;
const DISCOUNT_ORDER_CACHE_TTL = 600000; // 10 minutos de cache

class CollectionManager {
  constructor() {
    this._root = document.querySelector('[data-collection-page]');
    if (!this._root) return;

    this._isLoading = false;
    this._applyTimer = null;
    this._fetchController = null;
    this._isLoadingPage = false;
    this._isPreloadingInitialGrid = false;
    this._isDiscountSorting = false;
    this._isRenderingDiscountBatch = false;
    this._discountOrder = [];
    this._discountCursor = 0;
    this._discountObserver = null;
    this._initialPreloadRunId = 0;
    this._pageSyncFrame = null;
    this._activePageUrl = '';

    this._onClick = this._onClick.bind(this);
    this._onChange = this._onChange.bind(this);
    this._onInput = this._onInput.bind(this);
    this._onPopState = this._onPopState.bind(this);
    this._onScroll = this._onScroll.bind(this);

    this._init();
  }

  _init() {
    document.addEventListener('click', this._onClick);
    document.addEventListener('change', this._onChange);
    document.addEventListener('input', this._onInput);
    window.addEventListener('popstate', this._onPopState);
    window.addEventListener('scroll', this._onScroll, { passive: true });

    this._syncAllPriceFilters();
    this._hydrateInitialPageGroup();
    this._initObserver();

    if (this._syncDiscountSortState()) {
      this._applyDiscountSort();
    } else {
      this._preloadInitialGrid();
    }

    this._scheduleVisiblePageSync();
  }

  _initObserver() {
    if (this._observer) {
      this._observer.disconnect();
    }

    this._observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this._isLoadingPage && !this._isPreloadingInitialGrid && !this._isDiscountSorting) {
          this._loadNextPage(entry.target);
        }
      });
    }, { rootMargin: '400px' });

    this._observeTrigger();
  }

  _observeTrigger() {
    const trigger = document.querySelector('[data-infinite-scroll]');
    if (trigger && this._observer) {
      this._observer.observe(trigger);
    }
  }

  _getNextPageUrl(source = document) {
    const trigger = source.querySelector('[data-infinite-scroll]');
    if (trigger?.dataset.nextUrl) return this._mergeActiveFilterParams(trigger.dataset.nextUrl);

    const nextLink = source.querySelector('[data-collection-pagination] .next a, [data-collection-pagination] a.next');
    return nextLink?.href ? this._mergeActiveFilterParams(nextLink.href) : '';
  }

  _mergeActiveFilterParams(url) {
    const pageUrl = new URL(url, window.location.origin);
    const filterKeys = new Set(
      Array.from(pageUrl.searchParams.keys()).filter((key) => key.startsWith('filter.'))
    );
    const activeFilters = [];
    const form = this._getForm();

    if (!form) return pageUrl.toString();

    const formData = new FormData(form);

    for (const [key, value] of formData.entries()) {
      if (key.startsWith('filter.') && value !== '') {
        activeFilters.push([key, value]);
        filterKeys.add(key);
      }
    }

    filterKeys.forEach((key) => pageUrl.searchParams.delete(key));
    activeFilters.forEach(([key, value]) => pageUrl.searchParams.append(key, value));

    return pageUrl.toString();
  }

  async _fetchPageDocument(url) {
    const response = await fetch(url, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const text = await response.text();
    return new DOMParser().parseFromString(text, 'text/html');
  }

  _appendPageDocument(doc, fallbackUrl) {
    const newGrid = doc.querySelector('[data-collection-grid]');
    const currentGrid = document.querySelector('[data-collection-grid]');

    if (!newGrid || !currentGrid) return;

    const pageGroup = this._buildPageGroup(doc, fallbackUrl, newGrid);

    if (pageGroup) {
      currentGrid.appendChild(pageGroup);
    }
  }

  _replacePagination(doc) {
    const newPagination = doc.querySelector('[data-collection-pagination]');
    const currentPagination = document.querySelector('[data-collection-pagination]');

    if (!newPagination || !currentPagination) return;

    const currentGrid = document.querySelector('[data-collection-grid]');
    const hasLoadedProducts = currentGrid?.querySelector('.collectionPage__gridItem');

    if (newPagination.classList.contains('collectionPage__empty') && hasLoadedProducts) {
      currentPagination.remove();
      return;
    }

    currentPagination.replaceWith(newPagination);
    this._observeTrigger();
  }

  async _preloadInitialGrid() {
    if (this._isPreloadingInitialGrid) return;

    const grid = document.querySelector('[data-collection-grid]');
    const currentPage = Number.parseInt(grid?.dataset.currentPage || '1', 10);
    const initialProductTarget = 12;

    if (!grid || currentPage !== 1 || grid.querySelectorAll('.collectionPage__gridItem').length >= initialProductTarget) {
      this._root.removeAttribute('data-initial-grid-loading');
      return;
    }

    const preloadRunId = ++this._initialPreloadRunId;
    this._isPreloadingInitialGrid = true;

    try {
      let nextUrl = this._getNextPageUrl();

      while (nextUrl && grid.querySelectorAll('.collectionPage__gridItem').length < initialProductTarget) {
        const firstPageUrl = new URL(nextUrl, window.location.origin);
        const firstPage = Number.parseInt(firstPageUrl.searchParams.get('page') || '2', 10);
        const totalPages = Number.parseInt(grid.dataset.totalPages || '0', 10);
        const batchUrls = [firstPageUrl.toString()];

        if (totalPages === 0 || firstPage < totalPages) {
          const secondPageUrl = new URL(firstPageUrl);
          secondPageUrl.searchParams.set('page', String(firstPage + 1));
          batchUrls.push(secondPageUrl.toString());
        }

        const pages = await Promise.all(batchUrls.map(async (pageUrl) => ({
          doc: await this._fetchPageDocument(pageUrl),
          pageUrl,
        })));

        if (preloadRunId !== this._initialPreloadRunId) return;

        pages.forEach(({ doc, pageUrl }) => {
          this._appendPageDocument(doc, pageUrl);
        });

        const lastPage = pages[pages.length - 1]?.doc;
        if (!lastPage) break;

        this._replacePagination(lastPage);
        nextUrl = this._getNextPageUrl(lastPage);
      }

      this._scheduleVisiblePageSync();
    } catch (error) {
      console.error('[CollectionManager] Initial collection preload failed:', error);
    } finally {
      if (preloadRunId === this._initialPreloadRunId) {
        this._root.removeAttribute('data-initial-grid-loading');
      }
      this._isPreloadingInitialGrid = false;
    }
  }

  async _loadNextPage(trigger) {
    if (this._isPreloadingInitialGrid || this._isDiscountSorting) return;

    const url = trigger.dataset.nextUrl ? this._mergeActiveFilterParams(trigger.dataset.nextUrl) : '';
    if (!url) return;

    this._isLoadingPage = true;
    trigger.classList.add('is-loading');

    try {
      const response = await fetch(url, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');

      this._appendPageDocument(doc, response.url);
      this._replacePagination(doc);

      this._scheduleVisiblePageSync();
      document.dispatchEvent(new CustomEvent('collection:updated', {
        detail: { action: 'pagination', url: response.url },
      }));

    } catch (error) {
      console.error('[CollectionManager] Infinite scroll failed:', error);
      trigger.classList.remove('is-loading');
    } finally {
      this._isLoadingPage = false;
    }
  }

  _hoistStylesheets(scope) {
    if (!scope) return;

    const headHrefs = new Set(
      Array.from(document.head.querySelectorAll('link[rel="stylesheet"]')).map((link) => link.getAttribute('href'))
    );

    scope.querySelectorAll('link[rel="stylesheet"]').forEach((link) => {
      const href = link.getAttribute('href');
      if (!href) return;

      if (headHrefs.has(href)) {
        link.remove();
        return;
      }

      headHrefs.add(href);
      document.head.appendChild(link);
    });
  }

  _isDiscountSortSelected() {
    const root = this._getRoot();
    if (root?.dataset.discountSortEnabled !== 'true') return false;

    try {
      return new URL(window.location.href).searchParams.get('sort_by') === DISCOUNT_SORT_VALUE;
    } catch (_error) {
      return false;
    }
  }

  _syncDiscountSortState() {
    const isSelected = this._isDiscountSortSelected();
    const sortInput = document.querySelector('[data-sort-input]');

    if (isSelected && sortInput) sortInput.value = DISCOUNT_SORT_VALUE;

    document.querySelectorAll('[data-sort-option]').forEach((option) => {
      const isDiscountOption = option.dataset.value === DISCOUNT_SORT_VALUE;

      if (isSelected) {
        option.classList.toggle('is-active', isDiscountOption);
      } else if (isDiscountOption) {
        option.classList.remove('is-active');
      }
    });

    return isSelected;
  }

  _getDiscountBaseUrl() {
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    url.searchParams.delete('sort_by');
    url.searchParams.delete('view');
    return url;
  }

  _getDiscountOrderCacheKey() {
    const url = this._getDiscountBaseUrl();
    url.searchParams.sort();
    return `collection-discount-order-v1:${url.pathname}?${url.searchParams.toString()}`;
  }

  _readDiscountOrderCache() {
    try {
      const cached = JSON.parse(sessionStorage.getItem(this._getDiscountOrderCacheKey()));
      if (!cached || Date.now() - cached.createdAt > DISCOUNT_ORDER_CACHE_TTL) return null;
      return Array.isArray(cached.order) ? cached.order : null;
    } catch (_error) {
      return null;
    }
  }

  _writeDiscountOrderCache(order) {
    try {
      sessionStorage.setItem(this._getDiscountOrderCacheKey(), JSON.stringify({ order, createdAt: Date.now() }));
    } catch (_error) {
      return;
    }
  }

  async _fetchDiscountOrderPage(page) {
    const url = this._getDiscountBaseUrl();
    url.searchParams.set('view', DISCOUNT_ORDER_VIEW);
    url.searchParams.set('page', String(page));

    const response = await fetch(this._mergeActiveFilterParams(url.toString()), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    return JSON.parse((await response.text()).trim());
  }

  async _fetchDiscountOrder(runId) {
    const cachedOrder = this._readDiscountOrderCache();
    if (cachedOrder) return cachedOrder;

    const firstPage = await this._fetchDiscountOrderPage(1);
    if (runId !== this._initialPreloadRunId) return [];

    const items = firstPage.items || [];
    const totalPages = Math.min(firstPage.pages || 1, DISCOUNT_ORDER_PAGE_LIMIT);
    const pendingPages = [];

    for (let page = 2; page <= totalPages; page += 1) {
      pendingPages.push(page);
    }

    for (let index = 0; index < pendingPages.length; index += DISCOUNT_ORDER_CONCURRENCY) {
      const batch = pendingPages.slice(index, index + DISCOUNT_ORDER_CONCURRENCY);
      const orderPages = await Promise.all(batch.map((page) => this._fetchDiscountOrderPage(page)));

      if (runId !== this._initialPreloadRunId) return [];

      orderPages.forEach((orderPage) => items.push(...(orderPage.items || [])));
    }

    const order = items
      .map((item, index) => ({ h: item.h, d: item.d, i: index }))
      .sort((a, b) => (b.d - a.d) || (a.i - b.i));

    this._writeDiscountOrderCache(order);

    return order;
  }

  async _fetchDiscountCards(handles) {
    const shopRoot = window.Shopify?.routes?.root || '/';
    const url = new URL(`${shopRoot}search`.replace(/\/{2,}/g, '/'), window.location.origin);

    url.searchParams.set('q', handles.join(' '));
    url.searchParams.set('type', 'product');
    url.searchParams.set('view', DISCOUNT_CARDS_VIEW);

    const response = await fetch(url.toString(), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    return response.text();
  }

  _renderDiscountTrigger() {
    const results = document.querySelector('[data-plp-results]');
    if (!results) return;

    let pagination = results.querySelector('[data-collection-pagination]');

    if (!pagination) {
      pagination = document.createElement('nav');
      results.appendChild(pagination);
    }

    pagination.className = 'collectionPage__pagination';
    pagination.setAttribute('data-collection-pagination', '');
    pagination.innerHTML = '<div class="collectionPage__infiniteScroll" data-discount-more><span class="collectionPage__spinner"></span></div>';

    this._observeDiscountTrigger();
  }

  _removeDiscountTrigger() {
    this._discountObserver?.disconnect();
    document.querySelector('[data-discount-more]')?.remove();
  }

  _observeDiscountTrigger() {
    this._discountObserver?.disconnect();

    const trigger = document.querySelector('[data-discount-more]');
    if (!trigger) return;

    this._discountObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) this._renderNextDiscountBatch();
      });
    }, { rootMargin: '400px' });

    this._discountObserver.observe(trigger);
  }

  async _renderNextDiscountBatch() {
    if (this._isRenderingDiscountBatch) return;
    if (!this._isDiscountSorting) return;

    if (this._discountCursor >= this._discountOrder.length) {
      this._removeDiscountTrigger();
      return;
    }

    const pageGroup = document.querySelector('[data-collection-page-group]');
    if (!pageGroup) return;

    const runId = this._initialPreloadRunId;
    const batch = this._discountOrder.slice(this._discountCursor, this._discountCursor + DISCOUNT_BATCH_SIZE);

    this._isRenderingDiscountBatch = true;
    document.querySelector('[data-discount-more]')?.classList.add('is-loading');

    try {
      const html = await this._fetchDiscountCards(batch.map((entry) => entry.h));
      if (runId !== this._initialPreloadRunId) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');

      this._hoistStylesheets(doc);

      doc.querySelectorAll('[data-product-card]').forEach((card) => {
        const gridItem = document.createElement('div');
        gridItem.className = 'collectionPage__gridItem';
        gridItem.appendChild(card);
        pageGroup.appendChild(gridItem);
      });

      this._discountCursor += batch.length;

      if (this._discountCursor >= this._discountOrder.length) {
        this._removeDiscountTrigger();
      }

      document.dispatchEvent(new CustomEvent('collection:pageview', {
        detail: { action: 'discount-sort' },
      }));
    } catch (error) {
      console.error('[CollectionManager] Discount card render failed:', error);
    } finally {
      this._isRenderingDiscountBatch = false;
      document.querySelector('[data-discount-more]')?.classList.remove('is-loading');
    }
  }

  async _applyDiscountSort() {
    const grid = document.querySelector('[data-collection-grid]');

    if (!grid) {
      this._root?.removeAttribute('data-initial-grid-loading');
      return;
    }

    if (Number.parseInt(grid.dataset.currentPage || '1', 10) > 1) {
      const firstPageUrl = new URL(window.location.href);
      firstPageUrl.searchParams.delete('page');
      this._applyFilters(firstPageUrl.toString());
      return;
    }

    const runId = ++this._initialPreloadRunId;

    this._isDiscountSorting = true;
    this._discountOrder = [];
    this._discountCursor = 0;
    this._root?.setAttribute('data-initial-grid-loading', '');

    try {
      const order = await this._fetchDiscountOrder(runId);

      if (runId !== this._initialPreloadRunId) return;
      if (order.length === 0) return;

      const pageGroup = grid.querySelector('[data-collection-page-group]');
      if (!pageGroup) return;

      this._discountOrder = order;

      this._hoistStylesheets(grid);
      pageGroup.replaceChildren();
      grid.querySelectorAll('[data-collection-page-group]').forEach((group) => {
        if (group !== pageGroup) group.remove();
      });

      this._renderDiscountTrigger();
      await this._renderNextDiscountBatch();
    } catch (error) {
      console.error('[CollectionManager] Discount sort failed:', error);
    } finally {
      if (runId === this._initialPreloadRunId) {
        this._root?.removeAttribute('data-initial-grid-loading');
      }
    }
  }

  _getRoot() {
    return document.querySelector('[data-collection-page]');
  }

  _getForm() {
    return document.querySelector('#CollectionFiltersForm');
  }

  _getDrawerShell() {
    return document.querySelector('[data-filter-shell]');
  }

  _getSortPanel() {
    return document.querySelector('[data-sort-panel]');
  }

  _getSortToggle() {
    return document.querySelector('[data-sort-toggle]');
  }

  _onClick(event) {
    const root = this._getRoot();
    if (!root) return;

    const filterOpen = event.target.closest('[data-filter-open]');
    if (filterOpen) {
      event.preventDefault();
      this._openDrawer();
      return;
    }

    const filterClose = event.target.closest('[data-filter-close]');
    if (filterClose) {
      event.preventDefault();
      this._closeDrawer();
      return;
    }

    const filterApply = event.target.closest('[data-filter-apply]');
    if (filterApply) {
      event.preventDefault();
      window.clearTimeout(this._applyTimer);
      this._applyTimer = null;
      this._applyFilters(null, { closeDrawerAfter: true });
      return;
    }

    const filterClear = event.target.closest('[data-filter-clear]');
    if (filterClear) {
      event.preventDefault();
      this._applyFilters(filterClear.href, { preserveDrawerOpen: true });
      return;
    }

    const sortToggle = event.target.closest('[data-sort-toggle]');
    if (sortToggle) {
      event.preventDefault();
      this._toggleSort();
      return;
    }

    const sortOption = event.target.closest('[data-sort-option]');
    if (sortOption) {
      event.preventDefault();
      const sortInput = document.querySelector('[data-sort-input]');
      if (sortInput) {
        sortInput.value = sortOption.dataset.value || '';
      }
      this._closeSort();
      this._applyFilters();
      return;
    }

    const sortPanel = this._getSortPanel();
    const sortTrigger = this._getSortToggle();
    if (sortPanel && sortTrigger && !sortPanel.hidden) {
      if (!sortPanel.contains(event.target) && !sortTrigger.contains(event.target)) {
        this._closeSort();
      }
    }
  }

  _onChange(event) {
    const form = this._getForm();
    if (!form || !form.contains(event.target)) return;

    if (event.target.matches('input[type="checkbox"]')) {
      this._scheduleApply();
    }
  }

  _onInput(event) {
    const priceFilter = event.target.closest('[data-price-filter]');
    if (!priceFilter) return;

    if (event.target.matches('[data-price-min-range], [data-price-max-range]')) {
      this._syncPriceFilter(priceFilter, event.target);
      this._scheduleApply(220);
    }
  }

  _onPopState() {
    this._applyFilters(window.location.href, { skipHistory: true });
  }

  _onScroll() {
    this._scheduleVisiblePageSync();
  }

  _scheduleApply(delay = 180) {
    window.clearTimeout(this._applyTimer);
    this._applyTimer = window.setTimeout(() => {
      this._applyFilters();
    }, delay);
  }

  _toggleSort() {
    const panel = this._getSortPanel();
    const toggle = this._getSortToggle();
    if (!panel || !toggle) return;

    const shouldOpen = panel.hidden;
    panel.hidden = !shouldOpen;
    toggle.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
  }

  _closeSort() {
    const panel = this._getSortPanel();
    const toggle = this._getSortToggle();
    if (!panel || !toggle) return;

    panel.hidden = true;
    toggle.setAttribute('aria-expanded', 'false');
  }

  _openDrawer() {
    const shell = this._getDrawerShell();
    const trigger = document.querySelector('[data-filter-open]');
    if (!shell) return;

    shell.hidden = false;
    requestAnimationFrame(() => {
      shell.classList.add('is-visible');
    });
    document.body.style.overflow = 'hidden';
    document.body.classList.add('filter-drawer-open');
    const section = shell.closest('.shopify-section');
    if (section) section.classList.add('collection-section-elevated');
    trigger?.setAttribute('aria-expanded', 'true');
  }

  _closeDrawer() {
    const shell = this._getDrawerShell();
    const trigger = document.querySelector('[data-filter-open]');
    if (!shell) return;

    shell.classList.remove('is-visible');
    window.setTimeout(() => {
      const currentShell = this._getDrawerShell();
      if (!currentShell || currentShell.classList.contains('is-visible')) return;
      currentShell.hidden = true;
    }, 260);
    document.body.style.overflow = '';
    document.body.classList.remove('filter-drawer-open');
    const section = shell.closest('.shopify-section');
    if (section) section.classList.remove('collection-section-elevated');
    trigger?.setAttribute('aria-expanded', 'false');
  }

  _syncAllPriceFilters(scope = document) {
    scope.querySelectorAll('[data-price-filter]').forEach((filter) => {
      this._syncPriceFilter(filter);
    });
  }

  _syncPriceFilter(filter, sourceInput = null) {
    const minRange = filter.querySelector('[data-price-min-range]');
    const maxRange = filter.querySelector('[data-price-max-range]');
    const minInput = filter.querySelector('[data-price-min-input]');
    const maxInput = filter.querySelector('[data-price-max-input]');
    const minLabel = filter.querySelector('[data-price-min-label]');
    const maxLabel = filter.querySelector('[data-price-max-label]');
    const slider = filter.querySelector('.collectionPage__priceSlider');

    if (!minRange || !maxRange || !minInput || !maxInput || !minLabel || !maxLabel || !slider) return;

    const absoluteMin = Number.parseFloat(filter.dataset.min || minRange.min || '0');
    const absoluteMax = Number.parseFloat(filter.dataset.max || maxRange.max || '0');
    const availableMin = Number.parseFloat(filter.dataset.availableMin || absoluteMin);
    const availableMax = Number.parseFloat(filter.dataset.availableMax || absoluteMax);

    if (!Number.isFinite(absoluteMin) || !Number.isFinite(absoluteMax) || absoluteMax < absoluteMin) return;

    let minValue = Number.parseFloat(minRange.value);
    let maxValue = Number.parseFloat(maxRange.value);

    if (!Number.isFinite(minValue)) minValue = absoluteMin;
    if (!Number.isFinite(maxValue)) maxValue = absoluteMax;

    if (sourceInput === minRange && minValue > maxValue) {
      maxValue = minValue;
    }

    if (sourceInput === maxRange && maxValue < minValue) {
      minValue = maxValue;
    }

    minValue = Math.max(absoluteMin, Math.min(minValue, absoluteMax));
    maxValue = Math.max(absoluteMin, Math.min(maxValue, absoluteMax));

    minRange.value = String(minValue);
    maxRange.value = String(maxValue);

    const rangeSpan = Math.max(absoluteMax - absoluteMin, 0);
    const epsilon = 0.005;
    const isStaticRange = rangeSpan <= epsilon;
    const isTightRange = !isStaticRange && rangeSpan <= Math.max(absoluteMax * 0.03, 5);

    minInput.value = minValue - availableMin > epsilon ? minValue.toFixed(2) : '';
    maxInput.value = availableMax - maxValue > epsilon ? maxValue.toFixed(2) : '';

    const start = isStaticRange ? 0 : ((minValue - absoluteMin) / rangeSpan) * 100;
    const end = isStaticRange ? 100 : ((maxValue - absoluteMin) / rangeSpan) * 100;

    slider.style.setProperty('--price-start', `${start}%`);
    slider.style.setProperty('--price-end', `${end}%`);
    slider.classList.toggle('is-static', isStaticRange);
    slider.classList.toggle('is-tight', isTightRange || isStaticRange);

    minRange.disabled = isStaticRange;
    maxRange.disabled = isStaticRange;

    const locale = filter.dataset.locale || 'pt-BR';
    const currency = filter.dataset.currency || 'BRL';
    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    minLabel.textContent = formatter.format(minValue);
    maxLabel.textContent = formatter.format(maxValue);
  }

  _buildUrl() {
    const form = this._getForm();
    if (!form) return window.location.href;

    const url = new URL(form.action || window.location.pathname, window.location.origin);
    const params = new URLSearchParams();
    const formData = new FormData(form);

    for (const [key, value] of formData.entries()) {
      if (value !== '') {
        params.append(key, value);
      }
    }

    url.search = params.toString();
    return url.toString();
  }

  _captureUiState() {
    const shell = this._getDrawerShell();
    const details = Array.from(document.querySelectorAll('[data-filter-group]'));

    return {
      drawerOpen: Boolean(shell && !shell.hidden),
      openDetails: details.map((detail, index) => (detail.open ? index : null)).filter((value) => value !== null),
    };
  }

  _restoreUiState(state, options = {}) {
    const details = Array.from(document.querySelectorAll('[data-filter-group]'));
    details.forEach((detail, index) => {
      detail.open = state.openDetails.includes(index);
    });

    if (options.closeDrawerAfter) {
      this._closeDrawer();
    } else if (state.drawerOpen || options.preserveDrawerOpen) {
      this._openDrawer();
    }
  }

  _prepareIncomingUiState(doc, state, options = {}) {
    const details = Array.from(doc.querySelectorAll('[data-filter-group]'));
    details.forEach((detail, index) => {
      detail.open = state.openDetails.includes(index);
    });

    if (!(state.drawerOpen || options.preserveDrawerOpen)) return;

    const shell = doc.querySelector('[data-filter-shell]');
    const trigger = doc.querySelector('[data-filter-open]');

    if (shell) {
      shell.hidden = false;
      shell.classList.add('is-visible');
    }

    trigger?.setAttribute('aria-expanded', 'true');
  }

  _setLoading(isLoading) {
    const root = this._getRoot();
    if (!root) return;

    this._isLoading = isLoading;
    root.classList.toggle('is-loading', isLoading);
  }

  _getAbsoluteUrl(url) {
    try {
      return new URL(url, window.location.origin).toString();
    } catch (_error) {
      return window.location.href;
    }
  }

  _getHeadMetadata(doc, fallbackUrl) {
    const absoluteUrl = this._getAbsoluteUrl(fallbackUrl);
    const title = doc.querySelector('title')?.textContent?.trim() || document.title;
    const canonicalUrl = absoluteUrl;
    const ogUrl = absoluteUrl;

    return {
      title,
      canonicalUrl,
      ogUrl,
      pageUrl: absoluteUrl,
    };
  }

  _hydrateInitialPageGroup() {
    const firstGroup = document.querySelector('[data-collection-page-group]');
    if (!firstGroup) return;

    const metadata = this._getHeadMetadata(document, window.location.href);

    firstGroup.dataset.pageUrl = metadata.pageUrl;
    firstGroup.dataset.pageCanonicalUrl = metadata.canonicalUrl;
    firstGroup.dataset.pageOgUrl = metadata.ogUrl;
    firstGroup.dataset.pageTitle = metadata.title;
  }

  _buildPageGroup(doc, fallbackUrl, grid) {
    const incomingGroup = grid.querySelector('[data-collection-page-group]');
    const pageGroup = incomingGroup || document.createElement('div');
    const metadata = this._getHeadMetadata(doc, fallbackUrl);

    if (!incomingGroup) {
      pageGroup.className = 'collectionPage__pageGroup';
      pageGroup.dataset.collectionPageGroup = '';

      Array.from(grid.children).forEach((child) => {
        pageGroup.appendChild(child);
      });
    }

    pageGroup.dataset.pageUrl = metadata.pageUrl;
    pageGroup.dataset.pageCanonicalUrl = metadata.canonicalUrl;
    pageGroup.dataset.pageOgUrl = metadata.ogUrl;
    pageGroup.dataset.pageTitle = metadata.title;

    return pageGroup;
  }

  _scheduleVisiblePageSync() {
    if (this._pageSyncFrame) return;

    this._pageSyncFrame = window.requestAnimationFrame(() => {
      this._pageSyncFrame = null;
      this._syncVisiblePage();
    });
  }

  _syncVisiblePage() {
    const pageGroups = Array.from(document.querySelectorAll('[data-collection-page-group]'));
    if (pageGroups.length === 0) return;

    const viewportAnchor = window.innerHeight * 0.35;
    let activeGroup = pageGroups[0];

    pageGroups.forEach((group) => {
      const rect = group.getBoundingClientRect();

      if (rect.top <= viewportAnchor) {
        activeGroup = group;
      }
    });

    this._activatePageGroup(activeGroup);
  }

  _activatePageGroup(group) {
    if (!group) return;

    const pageUrl = group.dataset.pageUrl;
    if (!pageUrl || pageUrl === this._activePageUrl) return;

    const isInitialPageview = !this._activePageUrl;
    this._activePageUrl = pageUrl;

    const title = group.dataset.pageTitle || document.title;
    const canonicalUrl = group.dataset.pageCanonicalUrl || pageUrl;
    const ogUrl = group.dataset.pageOgUrl || canonicalUrl;

    window.history.replaceState({ path: pageUrl }, '', pageUrl);
    this._syncSeoState({ title, canonicalUrl, ogUrl });
    if (!isInitialPageview) {
      document.dispatchEvent(new CustomEvent('collection:pageview', {
        detail: { action: 'pagination', url: pageUrl },
      }));
    }
  }

  _syncSeoState({ title, canonicalUrl, ogUrl }) {
    if (title) {
      document.title = title;
    }

    document.querySelectorAll('link[rel="canonical"]').forEach((canonical) => {
      if (!canonicalUrl) return;
      canonical.setAttribute('href', this._getAbsoluteUrl(canonicalUrl));
      canonical.href = this._getAbsoluteUrl(canonicalUrl);
    });

    const ogUrlTag = document.querySelector('meta[property="og:url"]');
    if (ogUrlTag && ogUrl) {
      ogUrlTag.setAttribute('content', this._getAbsoluteUrl(ogUrl));
      ogUrlTag.content = this._getAbsoluteUrl(ogUrl);
    }
  }

  async _applyFilters(customUrl = null, options = {}) {
    this._initialPreloadRunId += 1;
    this._isDiscountSorting = false;
    this._discountObserver?.disconnect();
    this._root?.removeAttribute('data-initial-grid-loading');

    if (this._isLoading && !options.skipAbort) {
      this._fetchController?.abort();
    }

    const url = customUrl || this._buildUrl();
    const uiState = this._captureUiState();

    this._fetchController = new AbortController();
    this._setLoading(true);

    try {
      const response = await fetch(url, {
        signal: this._fetchController.signal,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');

      this._prepareIncomingUiState(doc, uiState, options);

      this._replaceElement('#CollectionFiltersForm', doc);
      this._replaceElement('[data-plp-count]', doc);
      this._replaceElement('[data-plp-results]', doc);

      const metadata = this._getHeadMetadata(doc, url);

      if (!options.skipHistory) {
        window.history.pushState({ path: url }, '', url);
      }

      this._activePageUrl = metadata.pageUrl;
      this._syncSeoState(metadata);
      this._restoreUiState(uiState, options);
      this._hydrateInitialPageGroup();
      this._syncAllPriceFilters(document);
      this._closeSort();
      this._observeTrigger();

      if (this._syncDiscountSortState()) {
        this._applyDiscountSort();
      }

      this._scheduleVisiblePageSync();
      document.dispatchEvent(new CustomEvent('collection:updated'));
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('[CollectionManager] Filter request failed:', error);
      }
    } finally {
      this._setLoading(false);
    }
  }

  _replaceElement(selector, doc) {
    const current = document.querySelector(selector);
    const incoming = doc.querySelector(selector);

    if (current && incoming) {
      current.replaceWith(incoming);
    }
  }

  destroy() {
    document.removeEventListener('click', this._onClick);
    document.removeEventListener('change', this._onChange);
    document.removeEventListener('input', this._onInput);
    window.removeEventListener('popstate', this._onPopState);
    window.removeEventListener('scroll', this._onScroll);
    if (this._observer) this._observer.disconnect();
    if (this._discountObserver) this._discountObserver.disconnect();
    if (this._pageSyncFrame) {
      window.cancelAnimationFrame(this._pageSyncFrame);
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.CollectionManager?.destroy?.();
  window.CollectionManager = new CollectionManager();
});




